import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchRoomProperties } from '../services/patientDetailsService';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  selectRoomProperties,
  selectRoomPropertiesLoaded,
  setRoomProperties
} from '../store/roomProperties/roomPropertiesSlice';
import { showError } from '../store/errorDialog/errorDialogSlice';

interface UseRoomPropertiesResult {
  // Whether the table has been fetched. Callers gate the patient *edit* form on
  // it, so it never seeds room needs from an incomplete table and wipes them on
  // save. Nothing else is withheld — room needs are optional.
  isLoaded: boolean;
  hasError: boolean;
  // Without this the failure is terminal: the effect's other dependencies
  // cannot change while the drawer stays mounted, so nothing would refetch.
  retry: () => void;
}

export function useRoomProperties(isOpen: boolean): UseRoomPropertiesResult {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const roomProperties = useAppSelector(selectRoomProperties);
  const isLoaded = useAppSelector(selectRoomPropertiesLoaded);
  const [hasError, setHasError] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!isOpen || roomProperties.length > 0) return;
    let cancelled = false;
    void fetchRoomProperties()
      .then((data) => { if (!cancelled) dispatch(setRoomProperties(data)); })
      .catch(() => {
        if (cancelled) return;
        setHasError(true);
        dispatch(showError({ message: t('fetch-error') }));
      });
    return () => { cancelled = true; };
  }, [isOpen, roomProperties.length, attempt, dispatch, t]);

  const retry = useCallback(() => {
    setHasError(false);
    setAttempt((previous) => previous + 1);
  }, []);

  return { isLoaded, hasError, retry };
}
